import { getColorLabel, getSizelabel, sortSizeOptions } from "~/helpers";

interface ElasticAggBucket {
  key: number | string;
  doc_count: number;
}

interface ElasticAgg {
  doc_count_error_upper_bound: number;
  sum_other_doc_count: number;
  buckets: ElasticAggBucket[];
}

interface ElasticData {
  [key: string]: ElasticAgg;
}

interface AggregationOptionItem {
  count: number;
  label: string;
  value: string;
  __typename: string;
}

interface AggregationOption {
  label: string;
  count: number;
  attribute_code: string;
  options: AggregationOptionItem[];
  position: number;
  __typename: string;
}

/**
 * Map Elasticsearch aggregation data to the frontend format
 */
export function mapAgg(elasticData: ElasticData): AggregationOption[] {
  const result: AggregationOption[] = [];
  
  // Field mapping configuration
  const fieldMapping: Record<string, string> = {
    'agg_terms_price': 'Price',
    'agg_terms_color_options': 'Color',
    'agg_terms_mastercolor_options': 'Mastercolor',
    'agg_terms_size_options': 'Size',
    'agg_terms_sizeguide': 'Sizeguide'
  };
  
  // Process each aggregation
  for (const [key, agg] of Object.entries(elasticData)) {
    // Skip if not in our mapping or no buckets
    if (!fieldMapping[key] || !agg.buckets || agg.buckets.length === 0) {
      continue;
    }
    
    const label = fieldMapping[key];
    const attribute_code = label.toLowerCase();
    
    let options: AggregationOptionItem[] = [];
    
    // Handle different fields with special formatting
    if (key === 'agg_terms_price') {
      options = agg.buckets.map(bucket => ({
        count: bucket.doc_count,
        label: `${bucket.key}`,
        value: `${bucket.key}`,
        __typename: 'AggregationOptionItem'
      }));
    }
    // Handle size options with getSizelabel
    else if (key === 'agg_terms_size_options') {
      options = agg.buckets.map(bucket => ({
        count: bucket.doc_count,
        label: getSizelabel(bucket.key),
        value: String(bucket.key),
        __typename: 'AggregationOptionItem'
      }));
      const labelValues = options.map(option => option.label);
      const sortedLabels = sortSizeOptions(labelValues);
      
      const sortedOptions = [];
      sortedLabels.forEach(sortedLabel => {
        const matchingOption = options.find(option => option.label === sortedLabel);
        if (matchingOption) {
          sortedOptions.push(matchingOption);
        }
      });
      
      options = sortedOptions;
    }
    else if (key === 'agg_terms_mastercolor_options') {
      options = agg.buckets.map(bucket => ({
        count: bucket.doc_count,
        label: getColorLabel(bucket.key),
        value: String(bucket.key),
        __typename: 'AggregationOptionItem'
      }));
    }
    // Handle other fields
    else {
      options = agg.buckets.map(bucket => ({
        count: bucket.doc_count,
        label: String(bucket.key),
        value: String(bucket.key),
        __typename: 'AggregationOptionItem'
      }));
    }
    
    result.push({
      label,
      count: agg.buckets.length,
      attribute_code,
      options,
      position: 0,
      __typename: 'AggregationOption'
    });
  }
  
  return result;
}
